import { useCallback, useEffect, useState } from 'react';
import { appointmentService } from '../../../../services/appointment.service';
import { AppointmentOptions, AppointmentResponseDto, AppointmentStatus } from '../../../../types/appointment.types';
import { toastService } from '../../../../services/toast.service';

const PAGE_SIZE_DEFAULT = 25;

function storageKey(clientId?: number) {
  return clientId ? `filters:client-${clientId}-appointments` : 'filters:expert-appointments';
}

function readStorageStatus(key: string): string {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw).status ?? '') : '';
  } catch { return ''; }
}

function writeStorageStatus(key: string, value: string) {
  try {
    const raw = localStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) : {};
    localStorage.setItem(key, JSON.stringify({ ...saved, status: value }));
  } catch {}
}

export function useExpertAppointments(clientId?: number) {
  const key = storageKey(clientId);

  const [appointments, setAppointments] = useState<AppointmentResponseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<AppointmentOptions>({ statuses: [], clients: [], experts: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => readStorageStatus(storageKey(clientId)));
  const [clientFilter, setClientFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_DEFAULT);

  const refreshOptions = useCallback(() => {
    appointmentService.getOptions('expert', clientId).then(setOptions).catch(() => {});
  }, [clientId]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentService.getExpertAppointments({
        page,
        limit,
        search: searchQuery || undefined,
        status: statusFilter as AppointmentStatus | '',
        clientId: clientId ?? (clientFilter ? Number(clientFilter) : undefined),
      });
      setAppointments(response.data);
      setTotal((response.meta ?? response.pagination)?.total ?? 0);
    } catch {
      toastService.error('Не вдалося завантажити список зустрічей.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, statusFilter, clientFilter, clientId]);

  useEffect(() => {
    fetchAppointments();
    refreshOptions();
  }, [fetchAppointments, refreshOptions]);

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('');
    setClientFilter('');
    setPage(1);
  };

  const onStatusChange = (v: string) => {
    setStatusFilter(v);
    setPage(1);
    writeStorageStatus(key, v);
  };

  return {
    appointments,
    total,
    loading,
    options,
    fetchAppointments,
    searchQuery,
    statusFilter,
    clientFilter,
    onSearchChange: (v: string) => { setSearchQuery(v); setPage(1); },
    onStatusChange,
    onClientChange: (v: string) => { setClientFilter(v); setPage(1); },
    page,
    limit,
    onPageChange: setPage,
    onLimitChange: (v: number) => { setLimit(v); setPage(1); },
    handleReset,
  };
}
